/**
 * AI Studio — thin shared API client for the AI Ads Studio and AI Movies & Series
 * tiles. Backed by `/api/ai-studio/*` which orchestrates kaharian_image +
 * kaharian_video + kaharian_render_mp4 under the hood.
 */
import axios from 'axios';
import { shrinkImage } from './shrinkImage';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/ai-studio`;

export const AI_STUDIO_CFG = {
  resolutions: ['720p', '1080p', '4k'],
  adAspects: ['9:16', '1:1', '16:9'],
  movieAspects: ['16:9', '9:16', '21:9'],
  durations: [4, 5, 6, 8, 10, 15, 20, 30],
};

export async function listProjects(kind) {
  const { data } = await axios.get(`${BASE}/projects`, { params: kind ? { kind } : {}, withCredentials: true });
  return data.projects || [];
}

export async function createProject(payload) {
  const { data } = await axios.post(`${BASE}/projects`, payload, { withCredentials: true });
  return data;
}

export async function getProject(id) {
  const { data } = await axios.get(`${BASE}/projects/${id}`, { withCredentials: true });
  return data;
}

export async function patchProject(id, patch) {
  const { data } = await axios.patch(`${BASE}/projects/${id}`, patch, { withCredentials: true });
  return data;
}

export async function deleteProject(id) {
  const { data } = await axios.delete(`${BASE}/projects/${id}`, { withCredentials: true });
  return data;
}

export async function addKitAsset(projectId, payload) {
  const { data } = await axios.post(`${BASE}/projects/${projectId}/kit`, payload, { withCredentials: true });
  return data;
}

export async function removeKitAsset(projectId, assetId) {
  const { data } = await axios.delete(`${BASE}/projects/${projectId}/kit/${assetId}`, { withCredentials: true });
  return data;
}

export async function addScene(projectId, payload) {
  const { data } = await axios.post(`${BASE}/projects/${projectId}/scenes`, payload, { withCredentials: true });
  return data;
}

export async function removeScene(projectId, sceneId) {
  const { data } = await axios.delete(`${BASE}/projects/${projectId}/scenes/${sceneId}`, { withCredentials: true });
  return data;
}

export async function generateStill(projectId, sceneId, payload) {
  const { data } = await axios.post(`${BASE}/projects/${projectId}/scenes/${sceneId}/still`, payload, { withCredentials: true });
  return data;
}

export async function approveStill(projectId, sceneId) {
  const { data } = await axios.post(`${BASE}/projects/${projectId}/scenes/${sceneId}/approve`, {}, { withCredentials: true });
  return data;
}

export async function renderScene(projectId, sceneId, payload) {
  const { data } = await axios.post(`${BASE}/projects/${projectId}/scenes/${sceneId}/render`, payload, { withCredentials: true });
  return data;
}

export async function exportProject(projectId, episode = 0) {
  const { data } = await axios.post(`${BASE}/projects/${projectId}/export`, { episode }, { withCredentials: true });
  return data;
}

export async function creditPreview(duration_seconds, resolution, engine = 'gateway') {
  const { data } = await axios.get(`${BASE}/credit-preview`, {
    params: { duration_seconds, resolution, engine },
    withCredentials: true,
  });
  return data;
}

// ─── AI Movies & Series — Director mode, production pipeline, voices ───
const cfg = { withCredentials: true };
export const absUrl = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u || '');
export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === 'object') return d.message || 'Something went wrong';
  return d || e?.message || 'Something went wrong';
};
export const TIER_RES = { draft: '720p', standard: '1080p', premium: '1080p' };

export async function getCatalog() { return (await axios.get(`${BASE}/catalog`, cfg)).data; }
export async function startAutoDirect(id, payload) { return (await axios.post(`${BASE}/projects/${id}/auto-direct`, payload, cfg)).data; }
export async function cancelJob(id, job) { return (await axios.post(`${BASE}/projects/${id}/cancel`, { job }, cfg)).data; }
export async function patchScene(id, sid, patch) { return (await axios.patch(`${BASE}/projects/${id}/scenes/${sid}`, patch, cfg)).data; }
export async function patchKitAsset(id, aid, patch) { return (await axios.patch(`${BASE}/projects/${id}/kit/${aid}`, patch, cfg)).data; }
export async function produceEstimate(id, params) { return (await axios.get(`${BASE}/projects/${id}/produce-estimate`, { ...cfg, params })).data; }
export async function costReport(id, episode = 0) { return (await axios.get(`${BASE}/projects/${id}/cost-report`, { ...cfg, params: { episode } })).data; }
export async function startProduction(id, payload) { return (await axios.post(`${BASE}/projects/${id}/produce`, payload, cfg)).data; }
export async function pollScene(id, sid) { return (await axios.post(`${BASE}/projects/${id}/scenes/${sid}/poll`, {}, cfg)).data; }
export async function pollExport(id) { return (await axios.post(`${BASE}/projects/${id}/export/poll`, {}, cfg)).data; }
export async function sceneVoiceover(id, sid, payload = {}) { return (await axios.post(`${BASE}/projects/${id}/scenes/${sid}/voiceover`, payload, cfg)).data; }
export async function listVoices() { return (await axios.get(`${BASE}/voices`, cfg)).data; }
export async function previewVoice(voiceId) { return (await axios.post(`${BASE}/voices/${voiceId}/preview`, {}, cfg)).data; }
export async function cloneVoice(formData) { return (await axios.post(`${API}/api/creative-studio/voice/clone`, formData, cfg)).data; }

// ─── Phase 1: Final Cut, Sound step, Studio Library, Google Drive ───
export async function finalCutHealth() { return (await axios.get(`${BASE}/final-cut/health`, cfg)).data; }
export async function startFinalCut(id, payload) { return (await axios.post(`${BASE}/projects/${id}/final-cut`, payload, cfg)).data; }
export async function startTrailer(id, payload) { return (await axios.post(`${BASE}/projects/${id}/trailer`, payload, cfg)).data; }
export async function voicesEstimate(id, episode = 0) { return (await axios.get(`${BASE}/projects/${id}/voices/estimate`, { ...cfg, params: { episode } })).data; }
export async function generateAllVoices(id, episode = 0) { return (await axios.post(`${BASE}/projects/${id}/voices/generate-all`, { episode }, cfg)).data; }
export async function uploadMusic(id, file) { const fd = new FormData(); fd.append('file', file); return (await axios.post(`${BASE}/projects/${id}/music`, fd, cfg)).data; }
export async function removeMusic(id) { return (await axios.delete(`${BASE}/projects/${id}/music`, cfg)).data; }
export async function composeAiScore(id) { return (await axios.post(`${BASE}/projects/${id}/music/ai`, {}, cfg)).data; }
export async function createShare(itemId, days) { return (await axios.post(`${BASE}/library/${itemId}/shares`, { expires_days: days }, cfg)).data; }
export async function listShares(itemId) { return (await axios.get(`${BASE}/library/${itemId}/shares`, cfg)).data; }
export async function revokeShare(token) { return (await axios.delete(`${BASE}/shares/${token}`, cfg)).data; }
export const autoScorePreviewUrl = (tone) => `${BASE}/music/auto-preview/${encodeURIComponent(tone || 'cinematic')}.mp3`;
export async function listLibrary() { return (await axios.get(`${BASE}/library`, cfg)).data; }
export async function deleteLibraryItem(id) { return (await axios.delete(`${BASE}/library/${id}`, cfg)).data; }
export async function saveItemToDrive(id) { return (await axios.post(`${BASE}/library/${id}/drive`, {}, cfg)).data; }
export async function saveLibrarySettings(s) { return (await axios.put(`${BASE}/library/settings`, s, cfg)).data; }
export async function driveStatus() { return (await axios.get(`${API}/api/drive/status`, cfg)).data; }
export async function driveConnectUrl() { return (await axios.get(`${API}/api/drive/connect`, { ...cfg, params: { return_to: 'library' } })).data.authorization_url; }
export async function driveDisconnect() { return (await axios.delete(`${API}/api/drive/disconnect`, cfg)).data; }

// ─── Always-On Voice ───
export async function voiceEngineHealth() { return (await axios.get(`${BASE}/voice-health`, cfg)).data; }
export async function upgradeVoice(id, sid) { return (await axios.post(`${BASE}/projects/${id}/scenes/${sid}/voice-upgrade`, {}, cfg)).data; }
export async function upgradeAllVoices(id) { return (await axios.post(`${BASE}/projects/${id}/voices/upgrade-all`, {}, cfg)).data; }
export async function listActors() { return (await axios.get(`${BASE}/actors`, cfg)).data; }
export async function patchActor(aid, patch) { return (await axios.patch(`${BASE}/actors/${aid}`, patch, cfg)).data; }
export async function deleteActor(aid) { return (await axios.delete(`${BASE}/actors/${aid}`, cfg)).data; }
export async function castActor(id, assetId, actorId) { return (await axios.post(`${BASE}/projects/${id}/kit/${assetId}/cast`, { actor_id: actorId }, cfg)).data; }
export const actorVoicePreviewUrl = (voiceId) => `${BASE}/actor-voices/${voiceId}/preview.mp3`;

// ─── Phase A: Director's Idea story, Asset Library ───
export async function writeStory(id, payload) { return (await axios.post(`${BASE}/projects/${id}/story`, payload, cfg)).data; }
export async function patchStory(id, patch) { return (await axios.patch(`${BASE}/projects/${id}/story`, patch, cfg)).data; }
export async function patchStoryScene(id, sid, patch) { return (await axios.patch(`${BASE}/projects/${id}/story/scenes/${sid}`, patch, cfg)).data; }
export async function addStoryScene(id, payload) { return (await axios.post(`${BASE}/projects/${id}/story/scenes`, payload, cfg)).data; }
export async function deleteStoryScene(id, sid) { return (await axios.delete(`${BASE}/projects/${id}/story/scenes/${sid}`, cfg)).data; }
export async function moveStoryScene(id, sid, direction) { return (await axios.post(`${BASE}/projects/${id}/story/scenes/${sid}/move`, { direction }, cfg)).data; }
export async function rewriteStoryScene(id, sid, instruction) { return (await axios.post(`${BASE}/projects/${id}/story/scenes/${sid}/rewrite`, { instruction }, cfg)).data; }
export async function shootStory(id, payload = {}) { return (await axios.post(`${BASE}/projects/${id}/story/shoot`, payload, cfg)).data; }
export async function listAssets(params = {}) { return (await axios.get(`${BASE}/assets`, { ...cfg, params })).data; }
export async function patchAsset(aid, patch) { return (await axios.patch(`${BASE}/assets/${aid}`, patch, cfg)).data; }
export async function deleteAsset(aid) { return (await axios.delete(`${BASE}/assets/${aid}`, cfg)).data; }
export async function musicFromAsset(id, assetId) { return (await axios.post(`${BASE}/projects/${id}/music/from-asset`, { asset_id: assetId }, cfg)).data; }
export async function uploadAsset(file, folder, { name = '', notes = '' } = {}) {
  const alpha = folder === 'logos' || (folder === 'props' && file.type === 'image/png');
  const small = file.type.startsWith('image/') ? await shrinkImage(file, { alpha }) : { file, width: 0, height: 0, original: file.size };
  const fd = new FormData();
  fd.append('file', small.file); fd.append('folder', folder); fd.append('name', name); fd.append('notes', notes);
  fd.append('width', String(small.width || 0)); fd.append('height', String(small.height || 0)); fd.append('original_size', String(small.original || file.size));
  return (await axios.post(`${BASE}/assets`, fd, cfg)).data;
}

// ─── MTV mode ───
export async function mtvGenres() { return (await axios.get(`${BASE}/mtv/genres`, cfg)).data; }
export async function makeMtvCover(id, payload) { return (await axios.post(`${BASE}/projects/${id}/mtv/cover`, payload, cfg)).data; }
export async function pickMtvVersion(id, versionId) { return (await axios.post(`${BASE}/projects/${id}/mtv/use`, { version_id: versionId }, cfg)).data; }
